import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed, effect, inject,
    input,
    model,
    output, signal, ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import {
    LabelSelectAddModel,
    LabelSelectModel,
} from '@client/app/feature-modules/shared/label/components/label-select/label-select.type';
import { MultiSelect, MultiSelectFilterEvent, MultiSelectLazyLoadEvent, MultiSelectModule } from 'primeng/multiselect';
import { FormsModule } from '@angular/forms';
import { ChipModule } from 'primeng/chip';
import { Button } from 'primeng/button';
import { LabelDialogService } from '@client/app/feature-modules/shared/label/services/label-dialog.service';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { cloneDeep, debounce, throttle } from 'lodash';
import { FilterService, OverlayOptions } from 'primeng/api';
import { NgClass, NgStyle } from '@angular/common';
import { AdaptiveColorPipe } from '@client/app/feature-modules/admin/modules/admin-labels/pipes/adaptive-color.pipe';
import { LabelTableService } from '@client/app/services/label-table/label-table.service';

@Component({
    selector: 'label-select',
    templateUrl: 'label-select.component.html',
    styleUrls: ['label-select.component.css'],
    standalone: true,
    imports: [
        MultiSelectModule,
        FormsModule,
        ChipModule,
        Button,
        TranslocoPipe,
        NgClass,
        NgStyle,
        AdaptiveColorPipe,
    ],
    providers: [
        LabelDialogService,
        FilterService,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class LabelSelectComponent implements AfterViewInit {
    private readonly labelDialogService = inject(LabelDialogService);
    private readonly translocoService = inject(TranslocoService);
    private readonly filterService = inject(FilterService);
    private readonly labelTableService = inject(LabelTableService);

    public canManageLabels: boolean;

    selected = model<LabelSelectModel[] | undefined>([]);

    labels = input<LabelSelectModel[]>([]);
    totalLabelsCount = input(0);
    width = input<number>(250);
    emptySearchMessage = input<string>(this.translocoService.translate('label.select.emptySearchMessage'));
    emptyMessage = input<string>(this.translocoService.translate('label.select.emptyMessage'));
    loading = input<boolean>(false);
    searchInputDebounceRate = input(500);
    updateRequestThrottleRate = input(1500);
    minSearchLength = input(0);
    pageSize = input(20);

    searchLabelInput = output<string>();
    labelAddRequest = output<LabelSelectAddModel>();
    labelsUpdateRequest = output<LabelSelectModel[]>();
    lazyLoad = output<{ page: number, pageSize: number }>();

    iconButtonWidth = signal<number>(0);
    initialSelected = signal<LabelSelectModel[]>([]);
    labelsCollapsed = signal(true);
    overlayVisible = signal(false);

    selectedLabelsContainerWidth = computed<number>(() => {
        return this.width() - this.iconButtonWidth();
    });
    visibleSelectedLabels = computed<LabelSelectModel[]>(() => {
        if (this.labelsCollapsed()) {
            return (this.selected() || []).slice(0, 2);
        }

        return this.selected() || [];
    });
    buttonIcon = computed<string>(() => {
        if ((this.selected() || []).length > 0) {
            return 'edit';
        }
        return 'add';
    });
    labelMultiSelectItems = computed<LabelSelectModel[]>(() => {
        const selectedMap = new Map<LabelSelectModel['id'], LabelSelectModel>();
        const combinedSelectedAndAvailableLabels: LabelSelectModel[] = [];
        if (this.loading()) {
            return [];
        }
        if (!this.searchQuery) {
            for (const label of this.initialSelected()) {
                selectedMap.set(label.id, label);
                combinedSelectedAndAvailableLabels.push(label);
            }
        }
        for (const label of this.labels()) {
            if (selectedMap.has(label.id)) {
                continue;
            }
            combinedSelectedAndAvailableLabels.push(label);
        }
        return combinedSelectedAndAvailableLabels
            .sort((a, b) => {
                if (selectedMap.has(a.id) && selectedMap.has(b.id)) {
                    return (a.name || '') < (b.name || '') ? -1 : 1;
                }
                if (selectedMap.has(a.id)) {
                    return 1;
                }
                if (selectedMap.has(b.id)) {
                    return 1;
                }

                return 0;
            });
    });

    @ViewChild('iconButton', { static: true }) iconButton: Button | null = null;
    @ViewChild('labelsMultiSelect', { static: true }) labelsMultiSelect: MultiSelect | null = null;

    searchQuery = '';
    overlayOptions: OverlayOptions = {
        mode: 'overlay',
        onBeforeShow: () => {
            this.firstPanelShow = true;
            this.overlayVisible.set(true);
        },
        onBeforeHide: () => {
            this.searchQuery = '';
            this.searchLabelInput.emit('');
            this.setInitialSelectedLabels();
            this.overlayVisible.set(false);
        },
    };
    enableVirtualScroll = true;
    firstPanelShow = false;

    constructor () {
        this.canManageLabels = this.labelTableService.canManageLabels;
        this.filterService.register('notFilter', () => true);

        effect(() => {
            if (this.width()) {
                this.updateIconButtonWidth();
            }
        });
        effect(() => {
            if (!this.loading() && this.firstPanelShow) {
                // Workaround for issue where the virtual scroller displays before the labels are fetched
                // When loading is finished and the overlay is showing up for the first time since last opened
                this.firstPanelShow = false;

                // Force re-render on the virtual scroller on the next detection cycle
                this.enableVirtualScroll = false;
                setTimeout(() => {
                    this.enableVirtualScroll = true;
                });
            }
        });
    }

    ngAfterViewInit () {
        this.setInitialSelectedLabels();
        this.updateIconButtonWidth();
    }

    setInitialSelectedLabels () {
        this.initialSelected.set(cloneDeep(this.selected() || []));
    }

    updateIconButtonWidth () {
        if (this.iconButton?.el.nativeElement) {
            this.iconButtonWidth.set(
                // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
                this.iconButton.el.nativeElement.clientWidth,
            );
        }
    }

    throttledRequestUpdateLabels = throttle((selected: LabelSelectModel[]) => {
        this.requestUpdateLabels(selected);
    }, this.updateRequestThrottleRate());
    requestUpdateLabels (selected: LabelSelectModel[]) {
        this.labelsUpdateRequest.emit(selected);
    }

    debouncedSearchLabelInputEmit = debounce((filter: string) => {
        if (
            filter.length > 0 &&
            this.minSearchLength() &&
            filter.length < this.minSearchLength()
        ) {
            return;
        }
        this.searchLabelInput.emit(filter);
    }, this.searchInputDebounceRate());
    onMultiSelectFilter ($event: MultiSelectFilterEvent) {
        if (typeof $event.filter !== 'string') {
            return;
        }

        const searchQuery = $event.filter.trim();

        this.searchQuery = searchQuery;
        this.debouncedSearchLabelInputEmit(searchQuery);
    }

    onAddLabel () {
        this.labelDialogService.openLabelDialog({
            model: null,
            header: {
                text: this.translocoService.translate('label.addDialog.header'),
            },
        })
            .onClose.subscribe(label => {
                if (!label) {
                    return;
                }
                this.labelAddRequest.emit({
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    color: label.colorCode,
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
                    name: label.labelName,
                });
            });
    }

    onClearLabels () {
        this.selected.set([]);
        this.throttledRequestUpdateLabels(this.selected() || []);
    }

    onLazyLoad ($event: MultiSelectLazyLoadEvent) {
        if (
            $event.last >= this.labelMultiSelectItems().length &&
            $event.last < this.totalLabelsCount()
        ) {
            const page = Math.floor($event.last / this.pageSize()) + 1;

            this.lazyLoad.emit({
                page,
                pageSize: this.pageSize(),
            });
        }
    }

    actionButtonAriaLabel (): string {
        if ((this.selected() || []).length > 0) {
            return this.translocoService.translate('label.select.edit');
        }
        return this.translocoService.translate('label.select.add');
    }

    focusToggleButton (buttonRef: Button) {
        setTimeout(() => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-member-access
            buttonRef.el?.nativeElement?.querySelector('button')?.focus();
        });
    }
}
